import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { isEnterprisePlan, PlanGroup } from '@d-id/types';

import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { useGetSupportUrl } from '@/hooks/useGetSupportUrl';
import warning from '@/images/WarningColored.svg';
import { useAuth } from '@/services/auth';
import { StyleCollection } from '@/utils/types';

interface Props {
    onClose: () => void;
    title: string;
    children: React.ReactNode;
}

const Infographic = styled.img`
    width: 2.4rem;
    height: 2.4rem;
    margin-inline-end: 1rem;
`;

const styles: StyleCollection = {
    generateButton: {
        fontSize: '1.6rem',
        alignItems: 'flex-start',
        flex: 1,
    },
    closeIcon: theme => ({
        position: 'absolute',
        top: 16,
        right: 16,
        color: theme.colors.black.main,
    }),
    contentStyle: {
        flex: 1,
        overflow: 'hidden',
    },
    rootStyle: {
        padding: '16px',
        borderRadius: '12px',
    },
};

export default function ({ onClose, title, children }: Props) {
    const { user } = useAuth();
    const supportUrl = useGetSupportUrl();
    const isReviewablePlan = (plan: PlanGroup | undefined) => isEnterprisePlan(plan) || plan === PlanGroup.ADVANCED;
    const buttonText = () => {
        if (isReviewablePlan(user?.planGroup)) {
            return 'Request manual review';
        }
        return 'I understand';
    };

    const callToAction = () => {
        if (isReviewablePlan(user?.planGroup)) {
            window.location.href = supportUrl;
        } else {
            onClose();
        }
    };
    return (
        <BootstrapDialog onClose={onClose} open={true} width="50rem" contentstyle={styles.contentStyle} rootstyle={styles.rootStyle}>
            <IconButton ariaLabel="close" onClick={onClose} sx={styles.closeIcon}>
                <CloseIcon />
            </IconButton>
            <DialogTitle data-testid="violation-modal" sx={{ height: '5rem', fontSize: '1.6rem' }}>
                <Infographic src={warning} />
                <Typography variant="h3">{title}</Typography>
            </DialogTitle>
            <DialogContent>{children}</DialogContent>
            <DialogActions>
                <Button autoFocus onClick={callToAction} variant="contained" color="secondary" sx={styles.generateButton}>
                    {buttonText()}
                </Button>
            </DialogActions>
        </BootstrapDialog>
    );
}
